import type { ReactNode } from 'react';
import { initialsOf } from './layout';
import { Dialog } from './overlay';
import { RadioCards } from './choice';

/**
 * Edit conflict (409) dialog (design/Konflikt, docs/10 "Konflikt"): your version and the current version side by
 * side, differing values highlighted, then a resolution: keep mine, take theirs or merge field by field.
 * Texts come from the caller; the caller performs the save for the chosen resolution.
 */
export type ConflictResolution = 'mine' | 'theirs' | 'merge';
export type ConflictField = { key: string; label: string; mine: string; theirs: string; differs: boolean };
export type ConflictVersion = { name: string; meta?: ReactNode; initials?: string; /** Replaces the initials, e.g. an icon when the other author is unknown. */ avatar?: ReactNode };

export function ConflictDialog({ open, onClose, title, context, intro, mine, theirs, fields, resolution, onResolution, picks, onPick, texts, impact, note, actions }: {
  open: boolean;
  onClose: () => void;
  title: string;
  context?: ReactNode;
  intro: ReactNode;
  mine: ConflictVersion;
  theirs: ConflictVersion;
  fields: ConflictField[];
  resolution: ConflictResolution;
  onResolution: (r: ConflictResolution) => void;
  /** Merge: chosen side per differing field. */
  picks: Record<string, 'mine' | 'theirs'>;
  onPick: (key: string, side: 'mine' | 'theirs') => void;
  texts: { applies: string; differs: string; resolveLabel: string; options: Record<ConflictResolution, { title: string; text: ReactNode }>; pickLabel: (field: string) => string; pickOption: (value: string, side: 'mine' | 'theirs') => string };
  impact?: ReactNode;
  note?: ReactNode;
  actions: ReactNode;
}) {
  const applies = (side: 'mine' | 'theirs') => resolution === side;
  const column = (side: 'mine' | 'theirs', v: ConflictVersion) => (
    <section className={`cf-col ${applies(side) ? 'applies' : ''} ${side}`} aria-label={v.name}>
      <div className="cf-head">
        <span className="cf-avatar" aria-hidden>{v.avatar ?? v.initials ?? initialsOf(v.name)}</span>
        <span className="cf-who"><strong>{v.name}</strong>{v.meta && <span>{v.meta}</span>}</span>
        {applies(side) && <span className="cf-badge">{texts.applies}</span>}
      </div>
      <dl className="cf-fields">
        {fields.map((f) => (
          <div key={f.key} className="cf-row">
            <dt>{f.label}</dt>
            <dd className={f.differs ? 'differs' : undefined}>{side === 'mine' ? f.mine : f.theirs}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
  const differing = fields.filter((f) => f.differs);
  return (
    <Dialog open={open} onClose={onClose} title={title} eyebrow={context} sectioned size="xl" icon={<span className="cf-icon" aria-hidden><svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M7 4 V20 M17 4 V20 M7 8 C12 8 12 16 17 16" /></svg></span>} actions={actions}>
      <div className="conflict dialog-pad stack">
        <p className="cf-intro">{intro}</p>
        <div className="cf-cols">
          {column('mine', mine)}
          {column('theirs', theirs)}
        </div>
        <span className="cf-legend"><span className="cf-legend-sw" aria-hidden />{texts.differs}</span>
        <RadioCards label={texts.resolveLabel} options={(['mine', 'theirs', 'merge'] as const).map((k) => ({ value: k, title: texts.options[k].title, sub: texts.options[k].text }))} value={resolution} onChange={onResolution} columns={3} />
        {resolution === 'merge' && differing.length > 0 && (
          <div className="cf-merge">
            {differing.map((f) => (
              <label key={f.key} className="field">
                <span>{texts.pickLabel(f.label)}</span>
                <select className="input" value={picks[f.key] ?? 'mine'} onChange={(e) => onPick(f.key, e.target.value as 'mine' | 'theirs')}>
                  <option value="mine">{texts.pickOption(f.mine, 'mine')}</option>
                  <option value="theirs">{texts.pickOption(f.theirs, 'theirs')}</option>
                </select>
              </label>
            ))}
          </div>
        )}
        {impact}
        {note}
      </div>
    </Dialog>
  );
}
